import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, X, Globe, DollarSign, Award, TrendingUp, Target, BookOpen, Eye, Volume2, User, Mail, Phone, Briefcase, Star, Settings, Brain, Mic, Database, FileText, Trash2, Copy, ChevronUp, ChevronDown, Upload, Grip, Shield, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email Config Form Component
function EmailConfigForm({ config, index, onUpdate, onRemove }) {
  const [testing, setTesting] = useState({ smtp: false, imap: false });
  const [testResults, setTestResults] = useState({ smtp: null, imap: null });

  const testSMTP = async () => {
    setTesting({ ...testing, smtp: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/test/email-configs/test-smtp`,
        config.smtp,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults({ ...testResults, smtp: response.data });
      toast.success(response.data.message || "SMTP test successful!");
    } catch (error) {
      const message = error.response?.data?.message || "SMTP test failed";
      setTestResults({ ...testResults, smtp: { success: false, message } });
      toast.error(message);
    } finally {
      setTesting({ ...testing, smtp: false });
    }
  };

  const testIMAP = async () => {
    setTesting({ ...testing, imap: true });
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/actors/test/email-configs/test-imap`,
        config.imap,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTestResults({ ...testResults, imap: response.data });
      toast.success(response.data.message || "IMAP test successful!");
    } catch (error) {
      const message = error.response?.data?.message || "IMAP test failed";
      setTestResults({ ...testResults, imap: { success: false, message } });
      toast.error(message);
    } finally {
      setTesting({ ...testing, imap: false });
    }
  };

  return (
    <Card className="border-2">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Email Account {index + 1}</CardTitle>
          <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Email Address */}
        <div>
          <Label>Email Address</Label>
          <Input
            type="email"
            placeholder="actor@example.com"
            value={config.email_address}
            onChange={(e) => onUpdate({ ...config, email_address: e.target.value })}
          />
        </div>

        {/* SMTP Configuration */}
        <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">SMTP (Outgoing Mail)</Label>
            <Button type="button" size="sm" onClick={testSMTP} disabled={testing.smtp}>
              {testing.smtp ? "Testing..." : "Test SMTP"}
            </Button>
          </div>
          {testResults.smtp && (
            <div className={`text-sm p-2 rounded ${testResults.smtp.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {testResults.smtp.message}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Host</Label>
              <Input
                placeholder="smtp.gmail.com"
                value={config.smtp.host}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, host: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input
                type="number"
                placeholder="587"
                value={config.smtp.port}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, port: parseInt(e.target.value) } })}
              />
            </div>
            <div>
              <Label className="text-sm">Username</Label>
              <Input
                placeholder="username"
                value={config.smtp.username}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, username: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={config.smtp.password}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, password: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Security</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={config.smtp.security}
                onChange={(e) => onUpdate({ ...config, smtp: { ...config.smtp, security: e.target.value } })}
              >
                <option value="TLS">TLS</option>
                <option value="SSL">SSL</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>
        </div>

        {/* IMAP Configuration */}
        <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">IMAP (Incoming Mail)</Label>
            <Button type="button" size="sm" onClick={testIMAP} disabled={testing.imap}>
              {testing.imap ? "Testing..." : "Test IMAP"}
            </Button>
          </div>
          {testResults.imap && (
            <div className={`text-sm p-2 rounded ${testResults.imap.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {testResults.imap.message}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Host</Label>
              <Input
                placeholder="imap.gmail.com"
                value={config.imap.host}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, host: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Port</Label>
              <Input
                type="number"
                placeholder="993"
                value={config.imap.port}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, port: parseInt(e.target.value) } })}
              />
            </div>
            <div>
              <Label className="text-sm">Username</Label>
              <Input
                placeholder="username"
                value={config.imap.username}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, username: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Password</Label>
              <Input
                type="password"
                placeholder="••••••••"
                value={config.imap.password}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, password: e.target.value } })}
              />
            </div>
            <div>
              <Label className="text-sm">Security</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={config.imap.security}
                onChange={(e) => onUpdate({ ...config, imap: { ...config.imap, security: e.target.value } })}
              >
                <option value="SSL">SSL</option>
                <option value="TLS">TLS</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>
        </div>

        {/* Sync Configuration */}
        <div className="space-y-3 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <Label className="text-base font-semibold">Sync Settings</Label>
          
          <div>
            <Label className="text-sm">Sync Interval</Label>
            <select
              className="w-full border rounded px-3 py-2"
              value={config.sync_interval || 60}
              onChange={(e) => onUpdate({ ...config, sync_interval: parseInt(e.target.value) })}
            >
              <option value={60}>Every 1 hour</option>
              <option value={120}>Every 2 hours</option>
              <option value={180}>Every 3 hours</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              How often should we check for new emails? Longer intervals improve performance.
            </p>
          </div>

          <div>
            <Label className="text-sm">Number of Emails to Fetch</Label>
            <select
              className="w-full border rounded px-3 py-2"
              value={config.fetch_limit || 200}
              onChange={(e) => onUpdate({ ...config, fetch_limit: parseInt(e.target.value) })}
            >
              <option value={100}>Fetch 100 emails</option>
              <option value={200}>Fetch 200 emails</option>
              <option value={400}>Fetch 400 emails</option>
              <option value={800}>Fetch 800 emails</option>
              <option value={1000}>Fetch 1000 emails</option>
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              How many of the latest emails should be synced from your inbox?
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AIActorFormPage() {
  const navigate = useNavigate();
  const { id } = useParams(); // Get actor ID from URL params for edit mode
  const isEditMode = !!id;

  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [openrouterModels, setOpenrouterModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [availableKBChunks, setAvailableKBChunks] = useState([]);
  const [loadingKBChunks, setLoadingKBChunks] = useState(false);
  const [showKBChunkSelector, setShowKBChunkSelector] = useState(false);
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [groupedVoices, setGroupedVoices] = useState({ cloned: [], professional: [], premade: [] });
  const [coachingNotes, setCoachingNotes] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [newCoachingNote, setNewCoachingNote] = useState({ category: '', content: '' });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteData, setEditingNoteData] = useState({ category: '', content: '' });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    about: "",
    tagline: "",
    work_profile: "",
    background: "",
    experience_level: "",
    default_llm_model: "google/gemini-2.0-flash-exp:free",
    fallback_models: [],
    system_prompt: "",
    guard_rail_enabled: true,
    guard_rail_strictness: "moderate",
    guard_rail_model: "google/gemini-2.0-flash-exp:free",
    custom_refusal_message: "",
    voice_mode: "openai",
    openai_voice_preference: "alloy",
    voice_id: "",
    voice_name: "",
    voice_speed: 1.0,
    voice_stability: 0.5,
    voice_similarity: 0.75,
    voice_style: 0.0,
    voice_speaker_boost: true,
    kb_similarity_threshold: 0.7,
    voice_ai_kb_item_limit: 100,
    selected_kb_chunk_ids: [],
    voice_profile_fields: ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
    voice_include_feedback: true,
    voice_feedback_types: ["user", "admin"],
    enable_tag_functions: false,
    filter_used_kb_items: false,
    chat_history_limit: 20,
    vad_sensitivity_threshold: 0.5,
    vad_silence_duration_ms: 700,
    enable_true_barge_in: false,
    ip_rate_limit_enabled: false,
    responsibilities: [],
    skills: [],
    social_media: [],
    projects: [],
    customer_reviews: [],
    is_public: false,
    hourly_rate: "",
    packages: [],
    verified_badges: [],
    availability_status: "Available",
    jobs_completed: 0,
    success_rate: 100,
    response_time: "Within 24 hours",
    video_url: "",
    case_studies: [],
    industries_served: [],
    languages: [],
    revenue_generated: "",
    time_saved_stats: "",
    credentials: [],
    active_projects_label: "Active Projects",
    active_projects_current: null,
    active_projects_total: null,
    availability_message: "",
    availability_badge_text: "Currently accepting projects",
    whats_included_items: [
      "Initial strategy consultation",
      "Custom implementation roadmap",
      "Ongoing support & optimization"
    ],
    cta_heading: "Let's Work Together",
    cta_description: "",
    cta_button_text: "Send an Inquiry",
    cta_button_color: "#0f172a",
    inquiry_form_fields: [
      { id: "name", label: "Name", type: "text", required: true, placeholder: "Your name" },
      { id: "email", label: "Email", type: "email", required: true, placeholder: "your@email.com" },
      { id: "message", label: "Project Description", type: "textarea", required: true, placeholder: "Tell us about your project..." }
    ],
    email_configs: [],
    public_profile_visibility: {
      experience_level: true,
      about: true,
      work_profile: true,
      background: true,
      skills: true,
      responsibilities: true,
      industries_served: true,
      languages: true,
      jobs_completed: true,
      success_rate: true,
      response_time: true,
      verified_badges: true,
      video_url: true,
      customer_reviews: true,
      case_studies: true,
      revenue_generated: true,
      time_saved_stats: true,
      credentials: true,
      pricing: true,
      packages: true,
      projects: true
    }
  });

  useEffect(() => {
    fetchOpenRouterModels();
    fetchVoices(); // Fetch voices on mount for both create and edit
    if (isEditMode) {
      fetchActorData();
      fetchCoachingNotes(id);
      fetchKBChunks(id);
    }
  }, [id]);

  const fetchActorData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const actor = response.data;
      setFormData({
        ...actor,
        voice_ai_kb_item_limit: actor.voice_ai_kb_item_limit || 100,
        selected_kb_chunk_ids: actor.selected_kb_chunk_ids || [],
        voice_profile_fields: actor.voice_profile_fields || ["about", "work_profile", "skills", "experience_level", "industries_served", "pricing", "case_studies", "customer_reviews"],
        voice_include_feedback: actor.voice_include_feedback !== undefined ? actor.voice_include_feedback : true,
        voice_feedback_types: actor.voice_feedback_types || ["user", "admin"],
        hourly_rate: actor.hourly_rate || "",
        public_profile_visibility: actor.public_profile_visibility || {
          experience_level: true,
          about: true,
          work_profile: true,
          background: true,
          skills: true,
          responsibilities: true,
          industries_served: true,
          languages: true,
          jobs_completed: true,
          success_rate: true,
          response_time: true,
          verified_badges: true,
          video_url: true,
          customer_reviews: true,
          case_studies: true,
          revenue_generated: true,
          time_saved_stats: true,
          credentials: true,
          pricing: true,
          packages: true,
          projects: true
        }
      });
      
      if (actor.profile_image) {
        setImagePreview(`${API}/ai/actors/images/${actor.profile_image}`);
      }
    } catch (error) {
      toast.error("Failed to fetch actor data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenRouterModels = async () => {
    try {
      setLoadingModels(true);
      const response = await axios.get(`${API}/openrouter/models`);
      setOpenrouterModels(response.data.models || []);
    } catch (error) {
      console.error("Failed to fetch OpenRouter models:", error);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!imageFile) return formData.profile_image;
    
    const formDataToSend = new FormData();
    formDataToSend.append('file', imageFile);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/ai/actors/upload-image`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data.image_path;
    } catch (error) {
      toast.error("Failed to upload image");
      throw error;
    }
  };

  const fetchVoices = async () => {
    setLoadingVoices(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/elevenlabs/voices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const voicesList = response.data.voices || [];
      setVoices(voicesList);
      
      // Group voices by category
      const grouped = {
        cloned: voicesList.filter(v => v.category === 'cloned'),
        professional: voicesList.filter(v => v.category === 'professional'),
        premade: voicesList.filter(v => v.category === 'premade' || !v.category)
      };
      setGroupedVoices(grouped);
    } catch (error) {
      console.error("Failed to fetch voices:", error);
    } finally {
      setLoadingVoices(false);
    }
  };

  const fetchKBChunks = async (actorId) => {
    setLoadingKBChunks(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/ai/actors/${actorId}/kb-chunks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableKBChunks(response.data.kb_chunks || []);
    } catch (error) {
      console.error("Failed to fetch KB chunks:", error);
      toast.error("Failed to load Knowledge Base chunks");
    } finally {
      setLoadingKBChunks(false);
    }
  };

  const fetchCoachingNotes = async (actorId) => {
    try {
      setLoadingNotes(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/ai-actors/${actorId}/coaching-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoachingNotes(response.data.coaching_notes || []);
    } catch (error) {
      console.error("Failed to fetch coaching notes:", error);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddCoachingNote = async () => {
    if (!newCoachingNote.category || !newCoachingNote.content) {
      toast.error("Please fill in both category and content");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/admin/ai-actors/${id}/coaching-notes`,
        newCoachingNote,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note added successfully");
      setNewCoachingNote({ category: '', content: '' });
      fetchCoachingNotes(id);
    } catch (error) {
      toast.error("Failed to add coaching note");
      console.error(error);
    }
  };

  const handleUpdateCoachingNote = async (noteId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/admin/ai-actors/${id}/coaching-notes/${noteId}`,
        editingNoteData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Coaching note updated successfully");
      setEditingNoteId(null);
      setEditingNoteData({ category: '', content: '' });
      fetchCoachingNotes(id);
    } catch (error) {
      toast.error("Failed to update coaching note");
      console.error(error);
    }
  };

  const handleDeleteCoachingNote = async (noteId) => {
    if (!window.confirm("Are you sure you want to delete this coaching note?")) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/ai-actors/${id}/coaching-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Coaching note deleted");
      fetchCoachingNotes(id);
    } catch (error) {
      toast.error("Failed to delete coaching note");
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Auto-set default model if not selected
    let submissionData = { ...formData };
    if (!submissionData.default_llm_model || submissionData.default_llm_model.trim() === "") {
      submissionData.default_llm_model = "google/gemini-2.0-flash-exp:free";
      console.log("Auto-selected default LLM model: google/gemini-2.0-flash-exp:free");
      // Also update the form state
      setFormData(prev => ({ ...prev, default_llm_model: "google/gemini-2.0-flash-exp:free" }));
    }
    
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      let imagePath = formData.profile_image;
      if (imageFile) {
        imagePath = await uploadImage();
      }
      
      let processedCredentials = formData.credentials;
      if (typeof formData.credentials === 'string') {
        processedCredentials = formData.credentials
          .split(",")
          .map(s => s.trim())
          .filter(s => s);
      }
      
      // Convert string fields to arrays if they're still strings
      const convertToArray = (value, separator = ',') => {
        if (typeof value === 'string') {
          return value.split(separator).map(s => s.trim()).filter(s => s);
        }
        return Array.isArray(value) ? value : [];
      };
      
      const processedArrayFields = {
        skills: convertToArray(formData.skills, ','),
        responsibilities: convertToArray(formData.responsibilities, ';'),
        industries_served: convertToArray(formData.industries_served, ','),
        languages: convertToArray(formData.languages, ','),
        verified_badges: convertToArray(formData.verified_badges, ',')
      };
      
      // Sanitize numeric fields - convert empty strings to null (backend-compatible)
      const sanitizeNumericField = (value) => {
        if (value === "" || value === null || value === undefined) {
          return null;  // Return null instead of undefined
        }
        const num = Number(value);
        return isNaN(num) ? null : num;
      };
      
      const sanitizedNumericFields = {
        hourly_rate: sanitizeNumericField(formData.hourly_rate),
        voice_speed: sanitizeNumericField(formData.voice_speed),
        voice_stability: sanitizeNumericField(formData.voice_stability),
        voice_similarity: sanitizeNumericField(formData.voice_similarity),
        voice_style: sanitizeNumericField(formData.voice_style),
        vad_sensitivity_threshold: sanitizeNumericField(formData.vad_sensitivity_threshold),
        vad_silence_duration_ms: sanitizeNumericField(formData.vad_silence_duration_ms),
        chat_history_limit: sanitizeNumericField(formData.chat_history_limit),
        voice_ai_kb_item_limit: sanitizeNumericField(formData.voice_ai_kb_item_limit),
        kb_similarity_threshold: sanitizeNumericField(formData.kb_similarity_threshold)
      };
      
      // Don't delete null values - they properly override empty strings
      // Backend accepts Optional[float] = None, so null is valid
      
      const actorData = {
        ...formData,
        profile_image: imagePath,
        credentials: processedCredentials,
        ...processedArrayFields,
        ...sanitizedNumericFields  // These will now properly override empty strings with null
      };
      
      if (isEditMode) {
        await axios.put(`${API}/ai/actors/${id}`, actorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("AI Actor updated successfully");
      } else {
        await axios.post(`${API}/ai/actors`, actorData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("AI Actor created successfully");
      }
      
      navigate('/ai/actors');
    } catch (error) {
      toast.error(isEditMode ? "Failed to update actor" : "Failed to create actor");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading actor data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            {isEditMode ? 'Edit AI Actor' : 'Create AI Actor'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEditMode ? 'Update the profile details for your digital human' : 'Fill in the profile details for your digital human'}
          </p>
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={loading}
          size="lg"
        >
          <Save className="w-4 h-4 mr-2" />
          {loading ? 'Saving...' : isEditMode ? 'Update Actor' : 'Create Actor'}
        </Button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="profile" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="profile-plus" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Profile Plus
            </TabsTrigger>
            {/* Tabs hidden as they are available in AI Actor Manage page */}
            {/* <TabsTrigger value="ai-config">AI Configuration</TabsTrigger> */}
            {/* <TabsTrigger value="voice">Voice & Knowledge Base</TabsTrigger> */}
            {/* <TabsTrigger value="email-config">Email Config</TabsTrigger> */}
            {/* <TabsTrigger value="advanced">Advanced</TabsTrigger> */}
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
                {/* Profile Image */}
                <div>
                  <Label>Profile Image</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-24 h-24 rounded-full object-cover border-2 border-slate-200"
                      />
                    )}
                    <Input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleImageChange}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                  <div>
                    <Label>Experience Level</Label>
                    <Input
                      value={formData.experience_level}
                      onChange={(e) => setFormData({...formData, experience_level: e.target.value})}
                      placeholder="Senior, Mid-level, Junior"
                    />
                  </div>
                </div>

                {/* About */}
                <div>
                  <Label>About</Label>
                  <Textarea
                    value={formData.about}
                    onChange={(e) => setFormData({...formData, about: e.target.value})}
                    placeholder="Brief introduction about the AI actor"
                    rows={6}
                    className="resize-y min-h-[150px]"
                  />
                </div>

                {/* Tagline */}
                <div>
                  <Label>Professional Tagline</Label>
                  <Input
                    value={formData.tagline}
                    onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                    placeholder="e.g., E-commerce Growth Expert, Marketing Strategist, etc."
                  />
                  <p className="text-xs text-slate-500 mt-1">Short professional tagline to display at top of public profile</p>
                </div>

                {/* Work Profile */}
                <div>
                  <Label>Work Profile</Label>
                  <Textarea
                    value={formData.work_profile}
                    onChange={(e) => setFormData({...formData, work_profile: e.target.value})}
                    placeholder="Professional work experience and expertise"
                    rows={6}
                    className="resize-y min-h-[150px]"
                  />
                </div>

                {/* Background */}
                <div>
                  <Label>Background</Label>
                  <Textarea
                    value={formData.background}
                    onChange={(e) => setFormData({...formData, background: e.target.value})}
                    placeholder="Educational and professional background"
                    rows={6}
                    className="resize-y min-h-[150px]"
                  />
                </div>

                {/* Skills */}
                <div>
                  <Label>Skills (comma-separated)</Label>
                  <Textarea
                    value={Array.isArray(formData.skills) ? formData.skills.join(", ") : (formData.skills || "")}
                    onChange={(e) => setFormData({
                      ...formData,
                      skills: e.target.value
                    })}
                    onBlur={(e) => {
                      // Convert to array on blur
                      if (typeof e.target.value === 'string') {
                        setFormData({
                          ...formData,
                          skills: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                        });
                      }
                    }}
                    placeholder="JavaScript, Python, Project Management"
                    rows={2}
                    className="resize-y"
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <Label>Responsibilities (semi-colon separated)</Label>
                  <Textarea
                    value={Array.isArray(formData.responsibilities) ? formData.responsibilities.join("; ") : (formData.responsibilities || "")}
                    onChange={(e) => setFormData({
                      ...formData,
                      responsibilities: e.target.value
                    })}
                    onBlur={(e) => {
                      // Convert to array on blur
                      if (typeof e.target.value === 'string') {
                        setFormData({
                          ...formData,
                          responsibilities: e.target.value.split(";").map(s => s.trim()).filter(s => s)
                        });
                      }
                    }}
                    placeholder="Team Leadership; Code Review; Architecture Design"
                    rows={2}
                    className="resize-y"
                  />
                </div>

                {/* Industries & Languages */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-indigo-600" />
                    Industries & Languages
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Industries Served (comma-separated)</Label>
                      <Textarea
                        value={Array.isArray(formData.industries_served) ? formData.industries_served.join(", ") : (formData.industries_served || "")}
                        onChange={(e) => setFormData({
                          ...formData,
                          industries_served: e.target.value
                        })}
                        onBlur={(e) => {
                          if (typeof e.target.value === 'string') {
                            setFormData({
                              ...formData,
                              industries_served: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                            });
                          }
                        }}
                        placeholder="E-commerce, Healthcare, Finance, Real Estate"
                        rows={2}
                        className="resize-y"
                      />
                    </div>

                    <div>
                      <Label>Languages (comma-separated)</Label>
                      <Textarea
                        value={Array.isArray(formData.languages) ? formData.languages.join(", ") : (formData.languages || "")}
                        onChange={(e) => setFormData({
                          ...formData,
                          languages: e.target.value
                        })}
                        onBlur={(e) => {
                          if (typeof e.target.value === 'string') {
                            setFormData({
                              ...formData,
                              languages: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                            });
                          }
                        }}
                        placeholder="English, Spanish, Portuguese"
                        rows={2}
                        className="resize-y"
                      />
                    </div>
                  </div>
                </div>

                {/* DEFAULT LLM MODEL - REQUIRED */}
          </TabsContent>

          {/* AI Configuration Tab */}
          <TabsContent value="ai-config" className="space-y-6">
                <div>
                  <Label>Default LLM Model</Label>
                  <Select
                    value={formData.default_llm_model}
                    onValueChange={(value) => setFormData({...formData, default_llm_model: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingModels ? "Loading models..." : "Select model (Gemini 2.0 Flash Free is default)"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px] overflow-y-auto">
                      {loadingModels ? (
                        <SelectItem value="loading" disabled>Loading models...</SelectItem>
                      ) : openrouterModels.length > 0 ? (
                        <>
                          {/* Group by provider - similar to API Keys settings */}
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Free Models</div>
                          {openrouterModels.filter(m => m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">OpenAI</div>
                          {openrouterModels.filter(m => m.id.startsWith('openai/') && !m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Anthropic</div>
                          {openrouterModels.filter(m => m.id.startsWith('anthropic/')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Google</div>
                          {openrouterModels.filter(m => m.id.startsWith('google/') && !m.id.includes(':free')).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                          
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Other Models</div>
                          {openrouterModels.filter(m => 
                            !m.id.startsWith('openai/') && 
                            !m.id.startsWith('anthropic/') && 
                            !m.id.startsWith('google/') &&
                            !m.id.includes(':free')
                          ).map((model) => (
                            <SelectItem key={model.id} value={model.id}>
                              {model.name || model.id}
                            </SelectItem>
                          ))}
                        </>
                      ) : (
                        <SelectItem value="none" disabled>No models available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    This model will be used for all AI activities performed by this actor unless overridden.
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                    <p className="text-sm text-blue-800">
                      💡 <strong>Don't worry about this now!</strong> A free, fast model (Gemini 2.0 Flash) is selected by default. 
                      You can change this anytime in <strong>Models & Settings</strong> after creating your actor.
                    </p>
                  </div>
                </div>

                {/* FALLBACK MODELS - MULTI-LEVEL PRIORITY CHAIN */}
                <Card className="border-2 border-amber-200 bg-amber-50/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-amber-600" />
                      Fallback Model Chain (Auto-Switching)
                    </CardTitle>
                    <CardDescription>
                      Configure multiple fallback models in priority order. If primary fails, system will try each model until one succeeds.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Display current fallback chain */}
                    <div className="space-y-2">
                      {formData.fallback_models && formData.fallback_models.length > 0 ? (
                        formData.fallback_models.map((model, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="bg-amber-100 text-amber-800">
                                {index + 1}
                              </Badge>
                              <span className="text-sm font-medium">{model}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {index > 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newModels = [...formData.fallback_models];
                                    [newModels[index], newModels[index - 1]] = [newModels[index - 1], newModels[index]];
                                    setFormData({...formData, fallback_models: newModels});
                                  }}
                                >
                                  ↑
                                </Button>
                              )}
                              {index < formData.fallback_models.length - 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newModels = [...formData.fallback_models];
                                    [newModels[index], newModels[index + 1]] = [newModels[index + 1], newModels[index]];
                                    setFormData({...formData, fallback_models: newModels});
                                  }}
                                >
                                  ↓
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newModels = formData.fallback_models.filter((_, i) => i !== index);
                                  setFormData({...formData, fallback_models: newModels});
                                }}
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No fallback models configured. Add models below.
                        </p>
                      )}
                    </div>

                    {/* Add new fallback model */}
                    {formData.fallback_models && formData.fallback_models.length < 5 && (
                      <div>
                        <Label>Add Fallback Model (up to 5)</Label>
                        <Select
                          value=""
                          onValueChange={(value) => {
                            if (value && !formData.fallback_models.includes(value) && value !== formData.default_llm_model) {
                              setFormData({
                                ...formData,
                                fallback_models: [...formData.fallback_models, value]
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select model to add" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[400px] overflow-y-auto">
                            {loadingModels ? (
                              <SelectItem value="loading" disabled>Loading models...</SelectItem>
                            ) : openrouterModels.length > 0 ? (
                              <>
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Recommended Free Models</div>
                                <SelectItem value="google/gemini-2.0-flash-exp:free">Google Gemini 2.0 Flash (Free)</SelectItem>
                                <SelectItem value="deepseek/deepseek-r1:free">DeepSeek R1 (Free)</SelectItem>
                                <SelectItem value="qwen/qwen-2.5-72b-instruct:free">Qwen 2.5 72B (Free)</SelectItem>
                                
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">All Free Models</div>
                                {openrouterModels.filter(m => m.id.includes(':free')).map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name || model.id}
                                  </SelectItem>
                                ))}
                                
                                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground mt-2">Paid Models</div>
                                {openrouterModels.filter(m => !m.id.includes(':free')).slice(0, 50).map((model) => (
                                  <SelectItem key={model.id} value={model.id}>
                                    {model.name || model.id}
                                  </SelectItem>
                                ))}
                              </>
                            ) : (
                              <SelectItem value="none" disabled>No models available</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Models will be tried in order: Primary → 1st Fallback → 2nd Fallback → etc.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* System Prompt */}
                <div>
                  <Label>System Prompt for Chat</Label>
                  <Textarea
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({...formData, system_prompt: e.target.value})}
                    placeholder="You are a helpful AI assistant specializing in..."
                    rows={15}
                    className="resize-y min-h-[300px] font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    This prompt will be combined with the actor's profile data to guide chat responses.
                  </p>
                </div>

                {/* Guard Rail Settings */}
                <Card className="border-2 border-indigo-200 bg-indigo-50/30">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-indigo-600" />
                      Guard Rail Settings (Expertise Boundary Protection)
                    </CardTitle>
                    <CardDescription>
                      Control how strictly the AI Actor stays within their expertise when chatting
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Guard Rail Enabled Toggle */}
                    <div className="flex items-center justify-between p-3 border rounded-lg bg-white">
                      <div className="flex-1">
                        <Label htmlFor="guard_rail_enabled" className="font-medium">
                          Enable Guard Rail
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1">
                          Pre-filter questions to ensure they align with this actor's expertise
                        </p>
                      </div>
                      <Switch
                        id="guard_rail_enabled"
                        checked={formData.guard_rail_enabled}
                        onCheckedChange={(checked) => setFormData({...formData, guard_rail_enabled: checked})}
                      />
                    </div>

                    {formData.guard_rail_enabled && (
                      <>
                        {/* Strictness Level */}
                        <div>
                          <Label>Strictness Level</Label>
                          <Select
                            value={formData.guard_rail_strictness}
                            onValueChange={(value) => setFormData({...formData, guard_rail_strictness: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select strictness level" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="strict">
                                <div className="flex flex-col">
                                  <span className="font-medium">Strict</span>
                                  <span className="text-xs text-muted-foreground">Only exact expertise matches</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="moderate">
                                <div className="flex flex-col">
                                  <span className="font-medium">Moderate (Recommended)</span>
                                  <span className="text-xs text-muted-foreground">Allow somewhat related questions</span>
                                </div>
                              </SelectItem>
                              <SelectItem value="loose">
                                <div className="flex flex-col">
                                  <span className="font-medium">Loose</span>
                                  <span className="text-xs text-muted-foreground">Block only obviously unrelated topics</span>
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Guard Rail Model */}
                        <div>
                          <Label>Guard Rail Model</Label>
                          <Select
                            value={formData.guard_rail_model}
                            onValueChange={(value) => setFormData({...formData, guard_rail_model: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select model for guard rail checks" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="google/gemini-2.0-flash-exp:free">
                                Google Gemini 2.0 Flash (Free)
                              </SelectItem>
                              <SelectItem value="openai/gpt-4o-mini">
                                OpenAI GPT-4o Mini (Paid - More Reliable)
                              </SelectItem>
                              <SelectItem value="anthropic/claude-3-haiku">
                                Anthropic Claude 3 Haiku (Paid - Fast)
                              </SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground mt-1">
                            Lightweight model used to check question relevance before main AI response
                          </p>
                        </div>

                        {/* Custom Refusal Message */}
                        <div>
                          <Label>Custom Refusal Message (Optional)</Label>
                          <Textarea
                            value={formData.custom_refusal_message}
                            onChange={(e) => setFormData({...formData, custom_refusal_message: e.target.value})}
                            placeholder="Leave blank for default message, or customize: 'I appreciate your question, but I focus exclusively on...'"
                            rows={4}
                            className="resize-y"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Message shown to users when their question is outside this actor's expertise
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Coaching Notes (Full CRUD Management) */}
                {isEditMode && (
                  <Card className="border-2 border-emerald-200 bg-emerald-50/30">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-emerald-600" />
                        Coaching Notes / Memory Bank (Admin Only)
                      </CardTitle>
                      <CardDescription>
                        Add, edit, and manage coaching notes to guide this AI Actor's responses
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Add New Note Form */}
                      <div className="border rounded-lg p-4 bg-white space-y-3">
                        <h4 className="font-semibold text-sm">Add New Coaching Note</h4>
                        <div>
                          <Label>Category</Label>
                          <Input
                            placeholder="e.g., pricing, technical, communication"
                            value={newCoachingNote.category}
                            onChange={(e) => setNewCoachingNote({...newCoachingNote, category: e.target.value})}
                          />
                        </div>
                        <div>
                          <Label>Content</Label>
                          <Textarea
                            placeholder="Add guidance or important information for this AI Actor..."
                            rows={5}
                            className="resize-y min-h-[120px]"
                            value={newCoachingNote.content}
                            onChange={(e) => setNewCoachingNote({...newCoachingNote, content: e.target.value})}
                          />
                        </div>
                        <Button
                          onClick={handleAddCoachingNote}
                          disabled={!newCoachingNote.category || !newCoachingNote.content}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Coaching Note
                        </Button>
                      </div>

                      {/* Existing Notes */}
                      {loadingNotes ? (
                        <p className="text-sm text-muted-foreground">Loading coaching notes...</p>
                      ) : coachingNotes.length === 0 ? (
                        <div className="p-4 border rounded-lg bg-white text-center">
                          <p className="text-sm text-muted-foreground">
                            No coaching notes yet. Add notes above to improve this AI Actor's responses.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Group by category */}
                          {Object.entries(
                            coachingNotes.reduce((acc, note) => {
                              const cat = note.category || 'Other';
                              if (!acc[cat]) acc[cat] = [];
                              acc[cat].push(note);
                              return acc;
                            }, {})
                          ).map(([category, notes]) => (
                            <div key={category} className="border rounded-lg p-3 bg-white">
                              <h4 className="font-semibold text-sm text-emerald-900 mb-2 uppercase">
                                {category}
                              </h4>
                              <div className="space-y-2">
                                {notes.map((note) => (
                                  <div key={note.id} className="p-2 bg-emerald-50 rounded">
                                    {editingNoteId === note.id ? (
                                      /* Edit Mode */
                                      <div className="space-y-2">
                                        <Input
                                          value={editingNoteData.category}
                                          onChange={(e) => setEditingNoteData({...editingNoteData, category: e.target.value})}
                                          placeholder="Category"
                                        />
                                        <Textarea
                                          value={editingNoteData.content}
                                          onChange={(e) => setEditingNoteData({...editingNoteData, content: e.target.value})}
                                          rows={3}
                                        />
                                        <div className="flex gap-2">
                                          <Button
                                            size="sm"
                                            onClick={() => handleUpdateCoachingNote(note.id)}
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                          >
                                            Save
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => {
                                              setEditingNoteId(null);
                                              setEditingNoteData({ category: '', content: '' });
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      /* View Mode */
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <p className="text-sm text-slate-700">{note.content}</p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            Added by {note.created_by} on {new Date(note.created_at).toLocaleDateString()}
                                          </p>
                                        </div>
                                        <div className="flex gap-1">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              setEditingNoteId(note.id);
                                              setEditingNoteData({
                                                category: note.category,
                                                content: note.content
                                              });
                                            }}
                                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDeleteCoachingNote(note.id)}
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        💡 You can also add coaching notes from Chat Moderation by viewing conversations
                      </p>
                    </CardContent>
                  </Card>
                )}


          </TabsContent>

          {/* Profile Plus Tab */}
          <TabsContent value="profile-plus" className="space-y-6">
                {/* SOCIAL MEDIA CREDENTIALS */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Globe className="w-5 h-5 text-blue-600" />
                    Social Media Credentials
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm text-muted-foreground">
                        Add social media accounts (credentials visible to admin only)
                      </Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData({
                          ...formData,
                          social_media: [...formData.social_media, {
                            platform: "",
                            url: "",
                            username: "",
                            password: ""
                          }]
                        })}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Platform
                      </Button>
                    </div>

                    {formData.social_media.map((social, socialIndex) => (
                      <div key={socialIndex} className="border rounded-lg p-4 mb-3 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-sm">{social.platform || `Platform ${socialIndex + 1}`}</h4>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newSocial = formData.social_media.filter((_, i) => i !== socialIndex);
                              setFormData({...formData, social_media: newSocial});
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm">Platform</Label>
                            <Select
                              value={social.platform}
                              onValueChange={(value) => {
                                const newSocial = [...formData.social_media];
                                newSocial[socialIndex].platform = value;
                                setFormData({...formData, social_media: newSocial});
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select platform" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                                <SelectItem value="Instagram">Instagram</SelectItem>
                                <SelectItem value="Facebook">Facebook</SelectItem>
                                <SelectItem value="Twitter">Twitter</SelectItem>
                                <SelectItem value="GitHub">GitHub</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label className="text-sm">Profile URL</Label>
                            <Input
                              placeholder="https://linkedin.com/in/username"
                              value={social.url}
                              onChange={(e) => {
                                const newSocial = [...formData.social_media];
                                newSocial[socialIndex].url = e.target.value;
                                setFormData({...formData, social_media: newSocial});
                              }}
                            />
                          </div>
                          
                          <div>
                            <Label className="text-sm">Username/Email</Label>
                            <div className="flex gap-2">
                              <Input
                                placeholder="username@email.com"
                                value={social.username}
                                onChange={(e) => {
                                  const newSocial = [...formData.social_media];
                                  newSocial[socialIndex].username = e.target.value;
                                  setFormData({...formData, social_media: newSocial});
                                }}
                                className="flex-1"
                              />
                              {social.username && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(social.username);
                                    toast.success("Username copied!");
                                  }}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div>
                            <Label className="text-sm">Password</Label>
                            <div className="flex gap-2">
                              <Input
                                type="password"
                                placeholder="••••••••"
                                value={social.password}
                                onChange={(e) => {
                                  const newSocial = [...formData.social_media];
                                  newSocial[socialIndex].password = e.target.value;
                                  setFormData({...formData, social_media: newSocial});
                                }}
                                className="flex-1"
                              />
                              {social.password && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    navigator.clipboard.writeText(social.password);
                                    toast.success("Password copied!");
                                  }}
                                >
                                  <Copy className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* PRICING SECTION */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-600" />
                    Pricing & Packages
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Hourly Rate ($)</Label>
                      <Input
                        type="number"
                        value={formData.hourly_rate}
                        onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                        placeholder="85"
                      />
                    </div>

                    {/* Pricing Packages */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Pricing Packages (Bronze/Silver/Gold)</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setFormData({
                            ...formData,
                            packages: [...formData.packages, {
                              name: "",
                              price: "",
                              description: "",
                              features: []
                            }]
                          })}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Package
                        </Button>
                      </div>

                      {formData.packages.map((pkg, pkgIndex) => (
                        <div key={pkgIndex} className="border rounded-lg p-4 mb-3 bg-slate-50">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-semibold text-sm">{pkg.name || `Package ${pkgIndex + 1}`}</h4>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const newPackages = formData.packages.filter((_, i) => i !== pkgIndex);
                                setFormData({...formData, packages: newPackages});
                              }}
                              className="text-red-600 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="grid grid-cols-3 gap-2 mb-2">
                            <Input
                              placeholder="Name (Bronze/Silver/Gold)"
                              value={pkg.name || ""}
                              onChange={(e) => {
                                const newPackages = [...formData.packages];
                                newPackages[pkgIndex].name = e.target.value;
                                setFormData({...formData, packages: newPackages});
                              }}
                            />
                            <Input
                              type="number"
                              placeholder="Price"
                              value={pkg.price || ""}
                              onChange={(e) => {
                                const newPackages = [...formData.packages];
                                newPackages[pkgIndex].price = e.target.value;
                                setFormData({...formData, packages: newPackages});
                              }}
                            />
                            <Input
                              placeholder="Description"
                              value={pkg.description || ""}
                              onChange={(e) => {
                                const newPackages = [...formData.packages];
                                newPackages[pkgIndex].description = e.target.value;
                                setFormData({...formData, packages: newPackages});
                              }}
                            />
                          </div>

                          <Textarea
                            placeholder="Features (one per line) - Press Enter for new line"
                            rows={3}
                            value={Array.isArray(pkg.features) ? pkg.features.join("\n") : ""}
                            onChange={(e) => {
                              const newPackages = [...formData.packages];
                              newPackages[pkgIndex].features = e.target.value.split("\n");
                              setFormData({...formData, packages: newPackages});
                            }}
                            className="text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* PERFORMANCE METRICS */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    Performance & Availability
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Availability Status</Label>
                      <Select
                        value={formData.availability_status}
                        onValueChange={(value) => setFormData({...formData, availability_status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Available">Available</SelectItem>
                          <SelectItem value="Busy">Busy</SelectItem>
                          <SelectItem value="Away">Away</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Jobs Completed</Label>
                      <Input
                        type="number"
                        value={formData.jobs_completed}
                        onChange={(e) => setFormData({...formData, jobs_completed: parseInt(e.target.value) || 0})}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <Label>Success Rate (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.success_rate}
                        onChange={(e) => setFormData({...formData, success_rate: parseFloat(e.target.value) || 100})}
                        placeholder="100"
                      />
                    </div>

                    <div>
                      <Label>Response Time</Label>
                      <Input
                        value={formData.response_time}
                        onChange={(e) => setFormData({...formData, response_time: e.target.value})}
                        placeholder="Within 24 hours"
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label>Verified Badges (comma-separated)</Label>
                    <Textarea
                      value={Array.isArray(formData.verified_badges) ? formData.verified_badges.join(", ") : (formData.verified_badges || "")}
                      onChange={(e) => setFormData({
                        ...formData,
                        verified_badges: e.target.value
                      })}
                      onBlur={(e) => {
                        if (typeof e.target.value === 'string') {
                          setFormData({
                            ...formData,
                            verified_badges: e.target.value.split(",").map(s => s.trim()).filter(s => s)
                          });
                        }
                      }}
                      placeholder="Top Rated, Fast Response, Verified Expert"
                      rows={2}
                      className="resize-y"
                    />
                  </div>

                  <div className="mt-4">
                    <Label>Video URL (YouTube/Vimeo)</Label>
                    <Input
                      value={formData.video_url}
                      onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                </div>

                {/* CASE STUDIES */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-indigo-600" />
                    Success Stories / Case Studies
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-600">Add before/after case studies showcasing your results</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData({
                          ...formData,
                          case_studies: [...formData.case_studies, {
                            title: "",
                            client_industry: "",
                            before: "",
                            after: "",
                            results: "",
                            metrics: ""
                          }]
                        })}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Case Study
                      </Button>
                    </div>

                    {formData.case_studies.map((study, studyIndex) => (
                      <div key={studyIndex} className="border rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-sm">Case Study {studyIndex + 1}</h4>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newStudies = formData.case_studies.filter((_, i) => i !== studyIndex);
                              setFormData({...formData, case_studies: newStudies});
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <Input
                            placeholder="Title (e.g., E-commerce Revenue Boost for RetailCo)"
                            value={study.title}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].title = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                          />
                          
                          <Input
                            placeholder="Client Industry (e.g., E-commerce, Healthcare)"
                            value={study.client_industry}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].client_industry = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                          />

                          <Textarea
                            placeholder="Before: Describe the problem/situation"
                            rows={2}
                            value={study.before}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].before = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                            className="text-sm"
                          />

                          <Textarea
                            placeholder="After: What changed/improved"
                            rows={2}
                            value={study.after}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].after = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                            className="text-sm"
                          />

                          <Textarea
                            placeholder="Results: Specific outcomes achieved"
                            rows={2}
                            value={study.results}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].results = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                            className="text-sm"
                          />

                          <Input
                            placeholder="Key Metric (e.g., 300% ROI, $120K saved)"
                            value={study.metrics}
                            onChange={(e) => {
                              const newStudies = [...formData.case_studies];
                              newStudies[studyIndex].metrics = e.target.value;
                              setFormData({...formData, case_studies: newStudies});
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CUSTOMER REVIEWS */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    Customer Reviews & Testimonials
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-slate-600">Add real client testimonials with ratings</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setFormData({
                          ...formData,
                          customer_reviews: [...formData.customer_reviews, {
                            customer_name: "",
                            company: "",
                            position: "",
                            rating: 5,
                            comment: "",
                            date: ""
                          }]
                        })}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Review
                      </Button>
                    </div>

                    {formData.customer_reviews.map((review, reviewIndex) => (
                      <div key={reviewIndex} className="border rounded-lg p-4 bg-slate-50">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-sm">Review {reviewIndex + 1}</h4>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              const newReviews = formData.customer_reviews.filter((_, i) => i !== reviewIndex);
                              setFormData({...formData, customer_reviews: newReviews});
                            }}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Customer Name"
                              value={review.customer_name}
                              onChange={(e) => {
                                const newReviews = [...formData.customer_reviews];
                                newReviews[reviewIndex].customer_name = e.target.value;
                                setFormData({...formData, customer_reviews: newReviews});
                              }}
                            />
                            
                            <Input
                              placeholder="Company"
                              value={review.company}
                              onChange={(e) => {
                                const newReviews = [...formData.customer_reviews];
                                newReviews[reviewIndex].company = e.target.value;
                                setFormData({...formData, customer_reviews: newReviews});
                              }}
                            />
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Position (e.g., CEO, Manager)"
                              value={review.position}
                              onChange={(e) => {
                                const newReviews = [...formData.customer_reviews];
                                newReviews[reviewIndex].position = e.target.value;
                                setFormData({...formData, customer_reviews: newReviews});
                              }}
                            />
                            
                            <div>
                              <Select
                                value={review.rating.toString()}
                                onValueChange={(value) => {
                                  const newReviews = [...formData.customer_reviews];
                                  newReviews[reviewIndex].rating = parseFloat(value);
                                  setFormData({...formData, customer_reviews: newReviews});
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Rating" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="5">⭐⭐⭐⭐⭐ (5 stars)</SelectItem>
                                  <SelectItem value="4.5">⭐⭐⭐⭐✨ (4.5 stars)</SelectItem>
                                  <SelectItem value="4">⭐⭐⭐⭐ (4 stars)</SelectItem>
                                  <SelectItem value="3.5">⭐⭐⭐✨ (3.5 stars)</SelectItem>
                                  <SelectItem value="3">⭐⭐⭐ (3 stars)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>

                          <Textarea
                            placeholder="Review comment / testimonial"
                            rows={3}
                            value={review.comment}
                            onChange={(e) => {
                              const newReviews = [...formData.customer_reviews];
                              newReviews[reviewIndex].comment = e.target.value;
                              setFormData({...formData, customer_reviews: newReviews});
                            }}
                            className="text-sm"
                          />

                          <Input
                            placeholder="Date (e.g., January 15, 2024)"
                            value={review.date}
                            onChange={(e) => {
                              const newReviews = [...formData.customer_reviews];
                              newReviews[reviewIndex].date = e.target.value;
                              setFormData({...formData, customer_reviews: newReviews});
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* TRUST INDICATORS */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-indigo-600" />
                    Trust & Credentials
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Revenue Generated for Clients</Label>
                      <Input
                        value={formData.revenue_generated}
                        onChange={(e) => setFormData({...formData, revenue_generated: e.target.value})}
                        placeholder="$2M+ in client revenue"
                      />
                    </div>

                    <div>
                      <Label>Time Saved Statistics</Label>
                      <Input
                        value={formData.time_saved_stats}
                        onChange={(e) => setFormData({...formData, time_saved_stats: e.target.value})}
                        placeholder="500+ hours saved for clients"
                      />
                    </div>

                    <div>
                      <Label>Credentials (comma-separated)</Label>
                      <Textarea
                        value={Array.isArray(formData.credentials) ? formData.credentials.join(", ") : (typeof formData.credentials === 'string' ? formData.credentials : "")}
                        onChange={(e) => setFormData({
                          ...formData,
                          credentials: e.target.value
                        })}
                        placeholder="MBA Harvard, AWS Certified, 10+ Years Experience"
                        rows={2}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Enter credentials separated by commas
                      </p>
                    </div>
                  </div>
                </div>

                {/* PUBLIC PROFILE CTA CUSTOMIZATION */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    Public Profile Call-to-Action
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Active Projects */}
                    <Card className="border-purple-200 bg-purple-50/30">
                      <CardHeader>
                        <CardTitle className="text-base">Active Projects Status</CardTitle>
                        <CardDescription>Show current workload and availability</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>Section Label</Label>
                          <Input
                            value={formData.active_projects_label}
                            onChange={(e) => setFormData({...formData, active_projects_label: e.target.value})}
                            placeholder="Active Projects"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Customize the label shown on the public profile (e.g., "Current Workload", "Project Capacity")
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Current Projects</Label>
                            <Input
                              type="number"
                              value={formData.active_projects_current || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                active_projects_current: e.target.value ? parseInt(e.target.value) : null
                              })}
                              placeholder="13"
                            />
                          </div>
                          <div>
                            <Label>Total Capacity</Label>
                            <Input
                              type="number"
                              value={formData.active_projects_total || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                active_projects_total: e.target.value ? parseInt(e.target.value) : null
                              })}
                              placeholder="15"
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Availability Badge Text</Label>
                          <Input
                            value={formData.availability_badge_text}
                            onChange={(e) => setFormData({...formData, availability_badge_text: e.target.value})}
                            placeholder="Currently accepting projects"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Badge text shown in the header when availability status is "Available"
                          </p>
                        </div>
                        <div>
                          <Label>Availability Message</Label>
                          <Input
                            value={formData.availability_message}
                            onChange={(e) => setFormData({...formData, availability_message: e.target.value})}
                            placeholder="Currently accepting 2 new clients this month"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Leave blank to hide the active projects section
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* What's Included */}
                    <Card className="border-purple-200 bg-purple-50/30">
                      <CardHeader>
                        <CardTitle className="text-base">What's Included Benefits</CardTitle>
                        <CardDescription>List of services/benefits for clients</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {formData.whats_included_items && formData.whats_included_items.map((item, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Input
                                value={item}
                                onChange={(e) => {
                                  const newItems = [...formData.whats_included_items];
                                  newItems[index] = e.target.value;
                                  setFormData({...formData, whats_included_items: newItems});
                                }}
                                placeholder="Enter benefit"
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newItems = formData.whats_included_items.filter((_, i) => i !== index);
                                  setFormData({...formData, whats_included_items: newItems});
                                }}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              whats_included_items: [...(formData.whats_included_items || []), ""]
                            })}
                            className="w-full"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Benefit
                          </Button>
                        </div>
                      </CardContent>
                    </Card>

                    {/* CTA Section */}
                    <Card className="border-purple-200 bg-purple-50/30">
                      <CardHeader>
                        <CardTitle className="text-base">Call-to-Action Section</CardTitle>
                        <CardDescription>Customize the inquiry section</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <Label>CTA Heading</Label>
                          <Input
                            value={formData.cta_heading}
                            onChange={(e) => setFormData({...formData, cta_heading: e.target.value})}
                            placeholder="Let's Work Together"
                          />
                        </div>
                        <div>
                          <Label>CTA Description</Label>
                          <Textarea
                            value={formData.cta_description}
                            onChange={(e) => setFormData({...formData, cta_description: e.target.value})}
                            placeholder="I'm currently accepting 2 new clients this month. Let's discuss how I can help your business grow."
                            rows={3}
                            className="resize-y"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Leave blank to use default availability message
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Button Text</Label>
                            <Input
                              value={formData.cta_button_text}
                              onChange={(e) => setFormData({...formData, cta_button_text: e.target.value})}
                              placeholder="Send an Inquiry"
                            />
                          </div>
                          <div>
                            <Label>Button Color</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={formData.cta_button_color}
                                onChange={(e) => setFormData({...formData, cta_button_color: e.target.value})}
                                className="w-20 h-10"
                              />
                              <Input
                                value={formData.cta_button_color}
                                onChange={(e) => setFormData({...formData, cta_button_color: e.target.value})}
                                placeholder="#0f172a"
                                className="flex-1"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Pick a color or enter hex code
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>

          </TabsContent>

          {/* Voice Tab */}
          <TabsContent value="voice" className="space-y-6">
                {/* Voice Configuration for Public Profile Calls */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-purple-600" />
                    <div>
                      <Label className="text-base font-semibold text-purple-900">Voice Call Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Configure voice settings for public profile "Start a Call" feature</p>
                    </div>
                  </div>

                  {/* Voice Mode Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-purple-900">Voice Mode</Label>
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="voice_mode"
                          value="openai"
                          checked={formData.voice_mode === "openai"}
                          onChange={(e) => setFormData({...formData, voice_mode: e.target.value})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm font-medium">⚡ OpenAI Voice (Fast, Low Latency)</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="voice_mode"
                          value="elevenlabs"
                          checked={formData.voice_mode === "elevenlabs"}
                          onChange={(e) => setFormData({...formData, voice_mode: e.target.value})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm font-medium">🎙️ ElevenLabs Voice (Custom Clone)</span>
                      </label>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formData.voice_mode === "openai" 
                        ? "OpenAI voices provide near real-time responses"
                        : "ElevenLabs uses your custom voice clone (requires voice assignment in Voice Settings)"
                      }
                    </p>
                  </div>

                  {/* OpenAI Voice Selection (when OpenAI mode is selected) */}
                  {formData.voice_mode === "openai" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">OpenAI Voice</Label>
                      <Select 
                        value={formData.openai_voice_preference} 
                        onValueChange={(value) => setFormData({...formData, openai_voice_preference: value})}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select OpenAI voice" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="alloy">Alloy (Neutral)</SelectItem>
                          <SelectItem value="echo">Echo (Male)</SelectItem>
                          <SelectItem value="fable">Fable (British Male)</SelectItem>
                          <SelectItem value="onyx">Onyx (Deep Male)</SelectItem>
                          <SelectItem value="nova">Nova (Female)</SelectItem>
                          <SelectItem value="shimmer">Shimmer (Soft Female)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* ElevenLabs Voice Selection & Settings (when ElevenLabs mode is selected) */}
                  {formData.voice_mode === "elevenlabs" && (
                    <div className="space-y-4">
                      {/* Voice Selection */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">ElevenLabs Voice</Label>
                        <Select 
                          value={formData.voice_id} 
                          onValueChange={(value) => {
                            const selectedVoice = voices.find(v => v.voice_id === value);
                            setFormData({
                              ...formData, 
                              voice_id: value,
                              voice_name: selectedVoice?.name || ""
                            });
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={loadingVoices ? "Loading voices..." : "Select a voice"} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {loadingVoices ? (
                              <SelectItem value="loading" disabled>Loading voices...</SelectItem>
                            ) : (
                              <>
                                {groupedVoices.cloned.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100">
                                      Your Voices ({groupedVoices.cloned.length})
                                    </div>
                                    {groupedVoices.cloned.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>⭐</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {groupedVoices.professional.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                      Professional Voices ({groupedVoices.professional.length})
                                    </div>
                                    {groupedVoices.professional.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>🎙️</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                                {groupedVoices.premade.length > 0 && (
                                  <>
                                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-slate-100 mt-1">
                                      Premade Voices ({groupedVoices.premade.length})
                                    </div>
                                    {groupedVoices.premade.map((voice) => (
                                      <SelectItem key={voice.voice_id} value={voice.voice_id}>
                                        <div className="flex items-center gap-2">
                                          <span>🔊</span>
                                          <span>{voice.name}</span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Voice Settings Sliders */}
                      {formData.voice_id && (
                        <div className="space-y-4 p-4 bg-white rounded-lg border border-purple-200">
                          <h4 className="text-sm font-semibold text-purple-900">Voice Settings</h4>
                          
                          {/* Speed */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Speed</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_speed.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0.5"
                              max="1.5"
                              step="0.1"
                              value={formData.voice_speed}
                              onChange={(e) => setFormData({...formData, voice_speed: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Stability */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Stability</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_stability.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_stability}
                              onChange={(e) => setFormData({...formData, voice_stability: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Similarity */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Similarity Boost</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_similarity.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_similarity}
                              onChange={(e) => setFormData({...formData, voice_similarity: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Style */}
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <Label className="text-xs">Style Exaggeration</Label>
                              <span className="text-xs text-muted-foreground">{formData.voice_style.toFixed(2)}</span>
                            </div>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={formData.voice_style}
                              onChange={(e) => setFormData({...formData, voice_style: parseFloat(e.target.value)})}
                              className="w-full"
                            />
                          </div>

                          {/* Speaker Boost */}
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Speaker Boost</Label>
                            <input
                              type="checkbox"
                              checked={formData.voice_speaker_boost}
                              onChange={(e) => setFormData({...formData, voice_speaker_boost: e.target.checked})}
                              className="w-4 h-4"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Knowledge Base Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-emerald-900">Knowledge Base Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Configure how the AI searches and uses Knowledge Base content</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-emerald-900">
                        Semantic Search Similarity Threshold
                      </Label>
                      <span className="text-sm font-mono text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                        {formData.kb_similarity_threshold === 0 ? "No Threshold" : `${(formData.kb_similarity_threshold * 100).toFixed(0)}%`}
                      </span>
                    </div>
                    
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={formData.kb_similarity_threshold}
                      onChange={(e) => setFormData({...formData, kb_similarity_threshold: parseFloat(e.target.value)})}
                      className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                    
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>0 (No Filter)</span>
                      <span>0.5 (Moderate)</span>
                      <span>1.0 (Very Strict)</span>
                    </div>
                    
                    <p className="text-xs text-muted-foreground bg-white p-3 rounded border border-emerald-200">
                      {formData.kb_similarity_threshold === 0 ? (
                        <span>⚠️ <strong>No threshold:</strong> Returns top results regardless of relevance. Use for general queries.</span>
                      ) : formData.kb_similarity_threshold < 0.5 ? (
                        <span>📖 <strong>Low threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns loosely related content. Good for exploratory questions.</span>
                      ) : formData.kb_similarity_threshold < 0.75 ? (
                        <span>🎯 <strong>Moderate threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns moderately relevant content. Balanced approach.</span>
                      ) : (
                        <span>🔍 <strong>High threshold ({(formData.kb_similarity_threshold * 100).toFixed(0)}%):</strong> Returns only highly relevant content. Best for specific queries.</span>
                      )}
                    </p>
                    
                    {/* KB Item Limit */}
                    <div className="pt-4 border-t border-emerald-200">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-emerald-900">
                          Voice AI KB Item Limit
                        </Label>
                        <span className="text-sm font-mono text-emerald-700 bg-emerald-100 px-2 py-1 rounded">
                          {formData.voice_ai_kb_item_limit} items
                        </span>
                      </div>
                      
                      <input
                        type="range"
                        min="10"
                        max="200"
                        step="10"
                        value={formData.voice_ai_kb_item_limit}
                        onChange={(e) => setFormData({...formData, voice_ai_kb_item_limit: parseInt(e.target.value)})}
                        className="w-full h-2 bg-emerald-200 rounded-lg appearance-none cursor-pointer accent-emerald-600 mt-2"
                      />
                      
                      <div className="flex justify-between text-xs text-slate-500 mt-1">
                        <span>10 (Fast)</span>
                        <span>100 (Balanced)</span>
                        <span>200 (Comprehensive)</span>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Maximum KB items to load at Voice AI session start. Higher = more context, slower loading.
                      </p>
                    </div>
                    
                    {/* KB Chunk Selection */}
                    <div className="pt-4 border-t border-emerald-200">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowKBChunkSelector(!showKBChunkSelector)}
                        className="w-full justify-between"
                        disabled={loadingKBChunks}
                      >
                        <span>
                          {showKBChunkSelector ? "Hide" : "Show"} KB Chunk Selection
                          {(formData.selected_kb_chunk_ids || []).length > 0 && ` (${(formData.selected_kb_chunk_ids || []).length} selected)`}
                        </span>
                        {loadingKBChunks && <span className="text-xs">Loading...</span>}
                      </Button>
                      
                      {showKBChunkSelector && (
                        <div className="mt-3 max-h-64 overflow-y-auto border border-emerald-200 rounded p-3 bg-white">
                          {(availableKBChunks || []).length === 0 ? (
                            <p className="text-xs text-slate-500 text-center py-4">
                              No KB chunks available. Associate Knowledge Bases first.
                            </p>
                          ) : (
                            <>
                              <div className="flex justify-between items-center mb-3 pb-2 border-b">
                                <p className="text-xs font-semibold text-emerald-900">
                                  Select specific chunks ({(formData.selected_kb_chunk_ids || []).length} of {(availableKBChunks || []).length})
                                </p>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if ((formData.selected_kb_chunk_ids || []).length === (availableKBChunks || []).length) {
                                      setFormData({...formData, selected_kb_chunk_ids: []});
                                    } else {
                                      setFormData({...formData, selected_kb_chunk_ids: (availableKBChunks || []).map(c => c.id)});
                                    }
                                  }}
                                  className="text-xs h-6"
                                >
                                  {(formData.selected_kb_chunk_ids || []).length === (availableKBChunks || []).length ? "Deselect All" : "Select All"}
                                </Button>
                              </div>
                              
                              <div className="space-y-2">
                                {(availableKBChunks || []).map((chunk) => (
                                  <label key={chunk.id} className="flex items-start gap-2 p-2 hover:bg-emerald-50 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={(formData.selected_kb_chunk_ids || []).includes(chunk.id)}
                                      onChange={(e) => {
                                        const currentIds = formData.selected_kb_chunk_ids || [];
                                        if (e.target.checked) {
                                          setFormData({
                                            ...formData,
                                            selected_kb_chunk_ids: [...currentIds, chunk.id]
                                          });
                                        } else {
                                          setFormData({
                                            ...formData,
                                            selected_kb_chunk_ids: currentIds.filter(id => id !== chunk.id)
                                          });
                                        }
                                      }}
                                      className="mt-1"
                                    />
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-slate-700">{chunk.source_name}</p>
                                      <p className="text-xs text-slate-500 line-clamp-2">{chunk.content_preview}</p>
                                      <Badge variant="outline" className="text-xs mt-1">{chunk.knowledge_base_name}</Badge>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Leave empty to auto-select chunks. Select specific chunks to manually control what's loaded.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Voice AI Profile & Feedback Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-blue-900">Voice AI Profile & Feedback Settings</Label>
                      <p className="text-xs text-slate-600 mt-1">Choose what information to include in voice conversations</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Profile Fields Selection */}
                    <div>
                      <Label className="text-sm font-medium text-blue-900 mb-2 block">Profile Fields to Include</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { value: "about", label: "About" },
                          { value: "work_profile", label: "Work Profile" },
                          { value: "skills", label: "Skills" },
                          { value: "experience_level", label: "Experience Level" },
                          { value: "industries_served", label: "Industries" },
                          { value: "pricing", label: "Pricing" },
                          { value: "case_studies", label: "Case Studies" },
                          { value: "customer_reviews", label: "Customer Reviews" }
                        ].map((field) => (
                          <label key={field.value} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100 hover:bg-blue-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(formData.voice_profile_fields || []).includes(field.value)}
                              onChange={(e) => {
                                const currentFields = formData.voice_profile_fields || [];
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    voice_profile_fields: [...currentFields, field.value]
                                  });
                                } else {
                                  setFormData({
                                    ...formData,
                                    voice_profile_fields: currentFields.filter(f => f !== field.value)
                                  });
                                }
                              }}
                            />
                            <span className="text-xs font-medium text-slate-700">{field.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Feedback Configuration */}
                    <div className="pt-3 border-t border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-sm font-medium text-blue-900">Include Feedback</Label>
                        <Switch
                          checked={formData.voice_include_feedback}
                          onCheckedChange={(checked) => setFormData({...formData, voice_include_feedback: checked})}
                        />
                      </div>
                      
                      {formData.voice_include_feedback && (
                        <div className="mt-2 space-y-2">
                          <Label className="text-xs text-slate-600">Feedback Types:</Label>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { value: "user", label: "User Feedback" },
                              { value: "admin", label: "Admin Reviews" },
                              { value: "positive", label: "Positive Only" },
                              { value: "negative", label: "Negative Only" }
                            ].map((type) => (
                              <label key={type.value} className="flex items-center gap-2 p-2 bg-white rounded border border-blue-100 hover:bg-blue-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={(formData.voice_feedback_types || []).includes(type.value)}
                                  onChange={(e) => {
                                    const currentTypes = formData.voice_feedback_types || [];
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        voice_feedback_types: [...currentTypes, type.value]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        voice_feedback_types: currentTypes.filter(t => t !== type.value)
                                      });
                                    }
                                  }}
                                />
                                <span className="text-xs font-medium text-slate-700">{type.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tag-Based Function Calling Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <div className="flex-1">
                      <Label className="text-base font-semibold text-amber-900">Tag-Based Function Calling (Fallback)</Label>
                      <p className="text-xs text-slate-600 mt-1">Enable automatic KB search by tags when static items don't have the answer</p>
                    </div>
                    <Switch
                      checked={formData.enable_tag_functions}
                      onCheckedChange={(checked) => setFormData({...formData, enable_tag_functions: checked})}
                    />
                  </div>

                  {formData.enable_tag_functions && (
                    <div className="space-y-3 pl-8">
                      <div className="bg-white p-4 rounded border border-amber-200">
                        <h4 className="text-sm font-semibold text-amber-900 mb-2">🎯 How It Works:</h4>
                        <ol className="space-y-2 text-xs text-amber-800">
                          <li className="flex gap-2">
                            <span className="font-bold">1.</span>
                            <span>Voice AI tries to answer from loaded KB items (static selection)</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">2.</span>
                            <span>If no answer found, AI automatically calls the search function</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">3.</span>
                            <span>System extracts keywords from user's question</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">4.</span>
                            <span>Searches KB items that have matching tags</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-bold">5.</span>
                            <span>Returns relevant items to AI for response</span>
                          </li>
                        </ol>
                        
                        <div className="mt-3 p-3 bg-amber-50 rounded border border-amber-300">
                          <p className="text-xs text-amber-900">
                            <strong>📋 Example:</strong><br/>
                            User asks: "What's your pricing?"<br/>
                            → AI extracts keywords: <code className="bg-amber-100 px-1 rounded">pricing, costs</code><br/>
                            → Searches KB items with tags: pricing, costs, packages, payment<br/>
                            → Returns matching items → AI responds with pricing info
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white p-3 rounded border border-amber-200">
                        <p className="text-xs text-amber-800">
                          <strong>⚠️ Important:</strong> Your KB items must have relevant tags assigned for this to work.
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          Go to <strong>Knowledge Base</strong> page → Edit items → Add tags like: pricing, services, company, technical, contact, etc.
                        </p>
                      </div>
                      
                      <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                        <p className="text-xs text-emerald-800">
                          <strong>✅ Benefits:</strong> Works with unlimited tags • Searches any keywords • No predefined categories • AI decides when to search
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Voice AI Conversation Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-purple-900">Voice AI Conversation Configuration</Label>
                      <p className="text-xs text-slate-600 mt-1">Control KB filtering and chat history for better conversations</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* KB Item Filtering Toggle */}
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-purple-100">
                      <div className="flex-1">
                        <Label className="text-sm font-medium text-purple-900">Filter Used KB Items</Label>
                        <p className="text-xs text-slate-600 mt-1">Prevent AI from repeating the same information by filtering out already-used KB items</p>
                      </div>
                      <Switch
                        checked={formData.filter_used_kb_items ?? false}
                        onCheckedChange={(checked) => setFormData({...formData, filter_used_kb_items: checked})}
                      />
                    </div>

                    {/* Chat History Limit */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-purple-900">Chat History Limit</Label>
                        <span className="text-sm font-semibold text-purple-600">{formData.chat_history_limit ?? 20} messages</span>
                      </div>
                      <input
                        type="range"
                        min="5"
                        max="50"
                        value={formData.chat_history_limit ?? 20}
                        onChange={(e) => setFormData({...formData, chat_history_limit: parseInt(e.target.value)})}
                        className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>5 (Minimal context)</span>
                        <span>25 (Balanced)</span>
                        <span>50 (Full history)</span>
                      </div>
                      <p className="text-xs text-slate-600">Number of previous messages to include in conversation context</p>
                    </div>
                  </div>
                </div>

                {/* Voice AI VAD Configuration */}
                <div className="space-y-4 p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-cyan-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <div>
                      <Label className="text-base font-semibold text-cyan-900">Voice Activity Detection (VAD) Settings</Label>
                      <p className="text-xs text-slate-600 mt-1">Adjust voice detection sensitivity and interruption behavior</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* VAD Sensitivity */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-cyan-900">Sensitivity Threshold</Label>
                        <span className="text-sm font-semibold text-cyan-600">{(formData.vad_sensitivity_threshold ?? 0.5).toFixed(2)}</span>
                      </div>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={formData.vad_sensitivity_threshold ?? 0.5}
                        onChange={(e) => setFormData({...formData, vad_sensitivity_threshold: parseFloat(e.target.value)})}
                        className="w-full h-2 bg-cyan-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>0.0 (Very sensitive)</span>
                        <span>0.5 (Balanced)</span>
                        <span>1.0 (Less sensitive)</span>
                      </div>
                      <p className="text-xs text-slate-600">How sensitive the system is to detecting speech. Lower = picks up quieter sounds</p>
                    </div>

                    {/* Silence Duration */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-cyan-900">Silence Duration</Label>
                        <span className="text-sm font-semibold text-cyan-600">{formData.vad_silence_duration_ms ?? 700}ms</span>
                      </div>
                      <input
                        type="range"
                        min="200"
                        max="2000"
                        step="100"
                        value={formData.vad_silence_duration_ms ?? 700}
                        onChange={(e) => setFormData({...formData, vad_silence_duration_ms: parseInt(e.target.value)})}
                        className="w-full h-2 bg-cyan-200 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                      />
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>200ms (Quick)</span>
                        <span>700ms (Normal)</span>
                        <span>2000ms (Patient)</span>
                      </div>
                      <p className="text-xs text-slate-600">How long to wait for silence before considering speech finished</p>
                    </div>

                    {/* True Barge-In Toggle */}
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-cyan-100">
                      <div className="flex-1">
                        <Label className="text-sm font-medium text-cyan-900">Enable True Barge-In (Interruption)</Label>
                        <p className="text-xs text-slate-600 mt-1">Allow users to interrupt AI while it's speaking</p>
                        <p className="text-xs text-amber-600 mt-1">⚠️ Experimental: May cause audio glitches</p>
                      </div>
                      <Switch
                        checked={formData.enable_true_barge_in ?? false}
                        onCheckedChange={(checked) => setFormData({...formData, enable_true_barge_in: checked})}
                      />
                    </div>
                  </div>
                </div>

          </TabsContent>

          {/* Email Config Tab */}
          <TabsContent value="email-config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Email Configuration
                </CardTitle>
                <CardDescription>
                  Configure SMTP and IMAP settings for this AI Actor to send and receive emails
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {formData.email_configs && formData.email_configs.map((config, index) => (
                  <EmailConfigForm
                    key={config.id || index}
                    config={config}
                    index={index}
                    onUpdate={(updatedConfig) => {
                      const newConfigs = [...formData.email_configs];
                      newConfigs[index] = updatedConfig;
                      setFormData({ ...formData, email_configs: newConfigs });
                    }}
                    onRemove={() => {
                      const newConfigs = formData.email_configs.filter((_, i) => i !== index);
                      setFormData({ ...formData, email_configs: newConfigs });
                    }}
                  />
                ))}
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const newConfig = {
                      id: Date.now().toString(),
                      email_address: '',
                      smtp: { host: '', port: 587, username: '', password: '', security: 'TLS' },
                      imap: { host: '', port: 993, username: '', password: '', security: 'SSL' },
                      is_active: true
                    };
                    setFormData({ ...formData, email_configs: [...(formData.email_configs || []), newConfig] });
                  }}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Email Account
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-6">
                {/* Public Profile Toggle */}
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-indigo-600" />
                    <div>
                      <Label className="text-base font-semibold">Make Profile Public</Label>
                      <p className="text-xs text-slate-500 mt-1">Allow anyone to view this actor's profile without login</p>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({...formData, is_public: checked})}
                  />
                </div>

                {/* Public Profile Visibility Settings */}
                {formData.is_public && (
                  <Card className="border-2 border-blue-200 bg-blue-50/30">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-blue-900">
                        <Eye className="w-5 h-5" />
                        Public Profile Visibility
                      </CardTitle>
                      <CardDescription>
                        Control which fields are visible on the public profile page
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        {/* Basic Information */}
                        <div className="col-span-2">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Basic Information</Label>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Experience Level</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.experience_level ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                experience_level: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">About</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.about ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                about: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Work Profile</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.work_profile ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                work_profile: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Background</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.background ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                background: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Skills</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.skills ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                skills: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Responsibilities</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.responsibilities ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                responsibilities: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Industries Served</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.industries_served ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                industries_served: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Languages</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.languages ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                languages: checked
                              }
                            })}
                          />
                        </div>

                        {/* Performance & Metrics */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Performance & Metrics</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Jobs Completed</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.jobs_completed ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                jobs_completed: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Success Rate</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.success_rate ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                success_rate: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Response Time</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.response_time ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                response_time: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Verified Badges</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.verified_badges ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                verified_badges: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Video</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.video_url ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                video_url: checked
                              }
                            })}
                          />
                        </div>

                        {/* Social Proof */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Social Proof & Portfolio</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Customer Reviews</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.customer_reviews ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                customer_reviews: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Case Studies</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.case_studies ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                case_studies: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Projects Portfolio</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.projects ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                projects: checked
                              }
                            })}
                          />
                        </div>

                        {/* Trust Indicators */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Trust & Achievements</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Revenue Generated</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.revenue_generated ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                revenue_generated: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Time Saved Stats</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.time_saved_stats ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                time_saved_stats: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Credentials</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.credentials ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                credentials: checked
                              }
                            })}
                          />
                        </div>

                        {/* Pricing */}
                        <div className="col-span-2 mt-4">
                          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Pricing Information</Label>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Hourly Rate</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.pricing ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                pricing: checked
                              }
                            })}
                          />
                        </div>

                        <div className="flex items-center justify-between p-3 bg-white rounded border">
                          <Label className="text-sm cursor-pointer">Pricing Packages</Label>
                          <Switch
                            checked={formData.public_profile_visibility?.packages ?? true}
                            onCheckedChange={(checked) => setFormData({
                              ...formData,
                              public_profile_visibility: {
                                ...formData.public_profile_visibility,
                                packages: checked
                              }
                            })}
                          />
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-blue-100 rounded border border-blue-200">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> Name and tagline are always visible on the public profile. Use these toggles to show/hide additional fields.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Inquiry Form Builder - Enhanced */}
                <Card className="border-2 border-green-200 bg-green-50/30 mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-900">
                      <FileText className="w-5 h-5" />
                      Inquiry Form Builder
                    </CardTitle>
                    <CardDescription>
                      Customize the fields shown on the inquiry form on the public profile. Drag to reorder fields.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Current Form Fields */}
                    <div className="space-y-3">
                      {formData.inquiry_form_fields && formData.inquiry_form_fields.map((field, index) => (
                        <Card key={field.id} className="border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              {/* Drag Handle & Reorder Buttons */}
                              <div className="flex flex-col items-center gap-1 pt-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={index === 0}
                                  onClick={() => {
                                    if (index > 0) {
                                      const newFields = [...formData.inquiry_form_fields];
                                      [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }
                                  }}
                                  className="h-6 w-6 p-0"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </Button>
                                <Grip className="w-4 h-4 text-muted-foreground" />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={index === formData.inquiry_form_fields.length - 1}
                                  onClick={() => {
                                    if (index < formData.inquiry_form_fields.length - 1) {
                                      const newFields = [...formData.inquiry_form_fields];
                                      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }
                                  }}
                                  className="h-6 w-6 p-0"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Field Configuration */}
                              <div className="flex-1 space-y-3">
                                {/* Row 1: Field ID and Type */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Field ID</Label>
                                    <Input
                                      value={field.id}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].id = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="field_name"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Field Type</Label>
                                    <Select
                                      value={field.type}
                                      onValueChange={(value) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].type = value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                    >
                                      <SelectTrigger className="text-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="text">Text</SelectItem>
                                        <SelectItem value="email">Email</SelectItem>
                                        <SelectItem value="tel">Phone</SelectItem>
                                        <SelectItem value="textarea">Textarea</SelectItem>
                                        <SelectItem value="number">Number</SelectItem>
                                        <SelectItem value="url">URL</SelectItem>
                                        <SelectItem value="select">Select Dropdown</SelectItem>
                                        <SelectItem value="checkbox">Checkbox</SelectItem>
                                        <SelectItem value="radio">Radio Buttons</SelectItem>
                                        <SelectItem value="file">File Upload</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                {/* Row 2: Label and Placeholder */}
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label className="text-xs">Label</Label>
                                    <Input
                                      value={field.label}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].label = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="Field Label"
                                      className="text-sm"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Placeholder</Label>
                                    <Input
                                      value={field.placeholder || ''}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].placeholder = e.target.value;
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder="Enter placeholder text"
                                      className="text-sm"
                                    />
                                  </div>
                                </div>

                                {/* Row 3: Help Text (Description) */}
                                <div>
                                  <Label className="text-xs">Help Text (Optional)</Label>
                                  <Input
                                    value={field.helpText || ''}
                                    onChange={(e) => {
                                      const newFields = [...formData.inquiry_form_fields];
                                      newFields[index].helpText = e.target.value;
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }}
                                    placeholder="Additional help text for the user"
                                    className="text-sm"
                                  />
                                </div>

                                {/* Conditional: Options for Select, Radio, Checkbox */}
                                {(field.type === 'select' || field.type === 'radio' || field.type === 'checkbox') && (
                                  <div>
                                    <Label className="text-xs">
                                      {field.type === 'checkbox' ? 'Options (comma-separated, for multiple checkboxes)' : 'Options (comma-separated)'}
                                    </Label>
                                    <Input
                                      value={field.options ? field.options.join(', ') : ''}
                                      onChange={(e) => {
                                        const newFields = [...formData.inquiry_form_fields];
                                        newFields[index].options = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                                        setFormData({...formData, inquiry_form_fields: newFields});
                                      }}
                                      placeholder={field.type === 'checkbox' ? 'Leave empty for single yes/no checkbox' : 'Option 1, Option 2, Option 3'}
                                      className="text-sm"
                                    />
                                  </div>
                                )}

                                {/* Conditional: File Upload Settings */}
                                {field.type === 'file' && (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Accepted File Types</Label>
                                      <Input
                                        value={field.acceptedFileTypes || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].acceptedFileTypes = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder=".pdf,.doc,.docx"
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Max File Size (MB)</Label>
                                      <Input
                                        type="number"
                                        value={field.maxFileSize || '5'}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].maxFileSize = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="5"
                                        className="text-sm"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Row 4: Validation Settings */}
                                {(field.type === 'text' || field.type === 'textarea' || field.type === 'number') && (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <Label className="text-xs">Min Length / Min Value</Label>
                                      <Input
                                        type="number"
                                        value={field.minLength || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].minLength = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="Optional"
                                        className="text-sm"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Max Length / Max Value</Label>
                                      <Input
                                        type="number"
                                        value={field.maxLength || ''}
                                        onChange={(e) => {
                                          const newFields = [...formData.inquiry_form_fields];
                                          newFields[index].maxLength = e.target.value;
                                          setFormData({...formData, inquiry_form_fields: newFields});
                                        }}
                                        placeholder="Optional"
                                        className="text-sm"
                                      />
                                    </div>
                                  </div>
                                )}

                                {/* Row 5: Required Switch */}
                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={field.required}
                                    onCheckedChange={(checked) => {
                                      const newFields = [...formData.inquiry_form_fields];
                                      newFields[index].required = checked;
                                      setFormData({...formData, inquiry_form_fields: newFields});
                                    }}
                                  />
                                  <Label className="text-xs">Required Field</Label>
                                </div>
                              </div>

                              {/* Delete Button */}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const newFields = formData.inquiry_form_fields.filter((_, i) => i !== index);
                                  setFormData({...formData, inquiry_form_fields: newFields});
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Add Field Button */}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        const newField = {
                          id: `field_${Date.now()}`,
                          label: "New Field",
                          type: "text",
                          required: false,
                          placeholder: "",
                          helpText: ""
                        };
                        setFormData({
                          ...formData,
                          inquiry_form_fields: [...(formData.inquiry_form_fields || []), newField]
                        });
                      }}
                      className="w-full border-green-200 text-green-700 hover:bg-green-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Form Field
                    </Button>

                    {/* Tips */}
                    <div className="p-3 bg-green-100 rounded border border-green-200">
                      <p className="text-xs text-green-800">
                        <strong>Tips:</strong><br/>
                        • Field IDs should be unique and use lowercase with underscores (e.g., company_name, project_budget)<br/>
                        • Use reorder buttons to change field sequence<br/>
                        • For checkboxes: leave options empty for a single yes/no checkbox, or add multiple options for checkbox group<br/>
                        • File uploads are stored temporarily and links will be included in submission data
                      </p>
                    </div>
                  </CardContent>
                </Card>

          </TabsContent>
        </Tabs>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      navigate("/ai/actors");
                      ;
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                    {isEditMode ? "Update" : "Create"} Actor
                  </Button>
                </div>
              </form>
    </div>
  );
}
